import { parse } from 'querystring';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { Loader } from '@/components/Loader';
import { ServerErrors } from '@/components/ServerErrors';
import { useMyWatches } from '@/hooks/useMyWatches';
import type { ApiErrorWithType } from '@/lib/types';
import { useRegionProvider } from '@/providers/RegionProvider';
import type { Watch } from '@/views/MyWatchesView';

import * as S from './QrScanner.style';

import * as M from '../../messages';

export interface QrReaderProps {
  onClose: () => void;
  onQrReaderClose: () => void;
  setSuccesses: (type: boolean) => void;
  setWatches: (watches: Watch[]) => void;
  watches: Watch[];
}

export const QrScanner = ({
  onClose,
  onQrReaderClose,
  setSuccesses,
  setWatches,
  watches,
}: QrReaderProps) => {
  const {
    query: { channel },
  } = useRegionProvider();
  const { formatMessage } = useIntl();
  const [text, setText] = useState<string>('');
  const [registerLoading, setRegisterLoading] = useState(false);
  const { addWatchToAccount, getWatchBySkuForSaleor, getWatchWarranty } =
    useMyWatches();
  const [errors, setErrors] = useState<ApiErrorWithType[]>([]);
  const Provider = useRegionProvider();
  useEffect(() => {
    const paramText = text.split('?')[1];
    const data = parse(paramText);
    if (data.SEC && data.SN) {
      setRegisterLoading(true);
      handleRegisterWatch(data.SN as string, data.SEC as string);
    }
  }, [text]);
  const containerStyle = {
    background: '#000000',
    height: '100%',
  };
  const handleRegisterWatch = async (sn: string, sec: string) => {
    // const addWatch = await registerWatch(model, sn, Provider);
    try {
      // get information about the product's SKU, disabling status, and whether it is bound.
      const res = await getWatchWarranty(sn, sec, channel);
      const { data } = res;

      if (data?.is_visible && !data.is_bind) {
        // get information about the product's name by saleor
        const product = await getWatchBySkuForSaleor(data.sku, Provider);

        if (product?.watchs?.edges && product.watchs?.edges.length === 0) {
          throw res;
        }
        const productName = product?.watchs?.edges?.[0]?.node?.product?.name;
        const subObj = {
          sn: sn,
          sec: sec,
          model: data.sku,
          country: Provider?.country.code,
          channel: Provider?.query.channel,
          product_name: productName,
        };
        const addWatch = await addWatchToAccount(subObj);
        if (addWatch.code === 200) {
          data.product_name = data.name;
          const arr = watches || [];
          arr?.push({ ...data, ...product?.watchs?.edges[0].node.product });
          setWatches(arr);
          setSuccesses(true);
          onQrReaderClose();
          onClose();
        } else {
          throw new Error(res.code);
        }
      } else {
        throw new Error(res.code);
      }
    } catch (error: any) {
      setRegisterLoading(false);

      setErrors([
        {
          code: 'INVALID',
          field: error.message,
          type: 'addWatch',
        },
      ]);
    }
  };

  return registerLoading ? (
    <Loader data-test-id='QrScannerLoader' height={640} />
  ) : (
    <S.QrReaderBox data-testid='scannerWrapper'>
      <ServerErrors
        data-test-id='QrScannerServerErrors'
        errors={errors}
        type='warning'
      />
      <S.Text data-test-id='QrScannerText'>
        {formatMessage(M.messages.scanQrCode)}
      </S.Text>
      <S.QrReaderWrapper
        constraints={{ facingMode: 'environment' }}
        containerStyle={containerStyle}
        data-test-id='QrScannerWrapper'
        onDecode={(result) => {
          setText(result);
        }}
        onError={(error) => console.log(error?.message)}
        videoStyle={{
          objectFit: 'cover',
        }}
      />
      <S.QrBox data-test-id='QrScannerBox'>
        <S.QrDivider data-test-id='QrScannerDivider' />
        <S.ButtonBox
          data-test-id='QrScannerButtonBox'
          onClick={onQrReaderClose}
        >
          {M.EnterSerialNumber}
        </S.ButtonBox>
      </S.QrBox>
    </S.QrReaderBox>
  );
};
